import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Save, Loader2, ListChecks } from "lucide-react";

const VALIDATIONS = [
  { value: "", label: "None" },
  { value: "pan", label: "PAN (ABCDE1234F)" },
  { value: "phone", label: "Phone (10 digits)" },
  { value: "aadhaar", label: "Aadhaar (12 digits)" },
  { value: "email", label: "Email" },
  { value: "pincode", label: "Pincode (6 digits)" },
  { value: "number", label: "Number" },
];

export default function FieldRules() {
  const [fields, setFields] = useState({ lead: [], partner: [] });
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/field-rules").then((r) => { setFields(r.data.fields); setRules(r.data.rules || {}); })
      .catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  const get = (scope, key) => rules?.[scope]?.[key] || {};
  const set = (scope, key, patch) => setRules((rs) => ({ ...rs, [scope]: { ...(rs[scope] || {}), [key]: { ...get(scope, key), ...patch } } }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/field-rules", { rules }); toast.success("Field rules saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const Table = ({ scope, list }) => (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">{scope === "lead" ? "New Lead Fields" : "New Partner Fields"}</div>
      <table className="w-full">
        <thead><tr className="text-xs text-slate-400 border-b border-slate-100"><th className="text-left px-4 py-2">Field</th><th className="px-4 py-2 w-28">Mandatory</th><th className="px-4 py-2 w-56 text-left">Validation</th></tr></thead>
        <tbody>
          {list.map((f) => (
            <tr key={f.key} data-testid={`fr-${scope}-${f.key}`} className="border-b border-slate-100 text-sm text-slate-700">
              <td className="px-4 py-2.5 font-medium text-slate-900">{f.label}</td>
              <td className="px-4 py-2.5 text-center"><input data-testid={`fr-req-${scope}-${f.key}`} type="checkbox" checked={!!get(scope, f.key).required} onChange={(e) => set(scope, f.key, { required: e.target.checked })} className="w-4 h-4 accent-emerald-600" /></td>
              <td className="px-4 py-2.5">
                <select data-testid={`fr-val-${scope}-${f.key}`} value={get(scope, f.key).validation || ""} onChange={(e) => set(scope, f.key, { validation: e.target.value })} className={inputCls}>
                  {VALIDATIONS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>;

  return (
    <div className="space-y-5" data-testid="field-rules-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><ListChecks size={24} className="text-emerald-600" /> Field Rules</h1>
          <p className="text-sm text-slate-500 mt-1">Mark New Lead / New Partner fields as mandatory and set validations</p>
        </div>
        <button data-testid="field-rules-save" onClick={save} disabled={saving} className={btnPrimary}>{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save</button>
      </div>
      <Table scope="lead" list={fields.lead} />
      <Table scope="partner" list={fields.partner} />
    </div>
  );
}
